import { useMemo } from "react";

import { SampleDataOverlay } from "./DiscoveryHubSampleState";
import OrgCompositeTrendChart from "./OrgCompositeTrendChart";
import { HubPageLoading } from "../../Components/EngagementEmptyPanel";
import {
  execStatusChipClass,
  statusChipFootClass,
  statusChipLabelClass,
  statusChipValueClass,
  statusChipValueUnitClass,
  STATUS_CHIP_ROW,
  tierBarFillClass,
  tierMixSegmentClass,
} from "../../Utils/Helper/discoveryHubUi";
import {
  LEADERBOARD_TIER_ORDER,
  fmtScore,
  formatQuarterPeriodLabel,
  tierDotClass,
  tierPillClass,
} from "../../Utils/Helper/sponsorEngagementUi";

export type ExecutiveSummaryPayload = {
  current_period?: string;
  current_publication_state?: string | null;
  display_period?: string;
  display_publication_state?: string | null;
  has_published_data?: boolean;
  prior_period?: string | null;
  tier_chart_current_period?: string | null;
  tier_chart_prior_period?: string | null;
  kpis?: {
    sponsor_count?: number;
    avg_composite?: number | null;
    avg_composite_delta?: number | null;
    champion_pct?: number;
    at_risk_pct?: number;
    champion_count?: number;
    at_risk_count?: number;
    completion_pct?: number;
    completion_done?: number;
    completion_total?: number;
  };
  tier_distribution_current?: Record<string, number>;
  tier_distribution_prior?: Record<string, number> | null;
  theater_breakdown?: {
    theater: string;
    count: number;
    avg_composite?: number | null;
    tiers: Record<string, number>;
  }[];
  top_sponsors?: {
    sponsor_id?: string;
    sponsor_name?: string;
    theater?: string;
    gl?: string;
    composite?: number;
    tier?: string;
    rank?: number;
    gap?: string;
  }[];
  at_risk_sponsors?: {
    sponsor_id?: string;
    sponsor_name?: string;
    theater?: string;
    gl?: string;
    composite?: number;
    tier?: string;
    rank?: number;
    gap?: string;
  }[];
  trend?: { period: string; avg_composite: number | null }[];
};

type ExecutiveSummaryPanelProps = {
  data: ExecutiveSummaryPayload | null;
  loading?: boolean;
};

const TierPill = ({ tier }: { tier: string }) => (
  <span
    className={tierPillClass(
      tier,
      "!text-[13px] !px-2.5 !py-1 !gap-1.5 !font-extrabold !inline-flex !w-[5.75rem] !justify-center"
    )}
  >
    <span className={tierDotClass(tier)} />
    {tier}
  </span>
);

const sponsorRowMetricsClass = "flex shrink-0 items-center gap-2.5";
const sponsorScoreColumnClass =
  "w-12 text-right text-base font-bold tabular-nums text-gray-900 leading-none";
const sponsorTierColumnClass = "flex w-24 justify-center";

const hasPublishedExecutiveData = (data: ExecutiveSummaryPayload | null) =>
  Boolean(
    data?.has_published_data ??
      (data?.display_publication_state === "published" &&
        (data?.kpis?.sponsor_count ?? 0) > 0)
  );

const sumTier = (dist: Record<string, number> | null | undefined) =>
  LEADERBOARD_TIER_ORDER.reduce((sum, tier) => sum + (dist?.[tier] ?? 0), 0);

const TREND_MIN_QUARTERS = 5;

const SAMPLE_EXECUTIVE_SUMMARY: ExecutiveSummaryPayload = {
  has_published_data: true,
  current_period: "2026-Q3",
  display_period: "2026-Q3",
  prior_period: "2026-Q2",
  tier_chart_current_period: "2026-Q3",
  tier_chart_prior_period: "2026-Q2",
  display_publication_state: "published",
  kpis: {
    sponsor_count: 12,
    avg_composite: 3.6,
    avg_composite_delta: 0.3,
    champion_pct: 33,
    at_risk_pct: 17,
    champion_count: 4,
    at_risk_count: 2,
    completion_pct: 75,
    completion_done: 9,
    completion_total: 12,
  },
  tier_distribution_current: {
    Champion: 4,
    Active: 3,
    Developing: 3,
    "At-Risk": 2,
  },
  tier_distribution_prior: {
    Champion: 3,
    Active: 4,
    Developing: 3,
    "At-Risk": 2,
  },
  theater_breakdown: [
    {
      theater: "Americas",
      count: 5,
      avg_composite: 3.8,
      tiers: { Champion: 2, Active: 2, Developing: 1, "At-Risk": 0 },
    },
    {
      theater: "EMEA",
      count: 4,
      avg_composite: 3.5,
      tiers: { Champion: 1, Active: 1, Developing: 2, "At-Risk": 0 },
    },
    {
      theater: "APJ",
      count: 3,
      avg_composite: 3.2,
      tiers: { Champion: 1, Active: 0, Developing: 0, "At-Risk": 2 },
    },
  ],
  top_sponsors: [
    {
      sponsor_name: "Alex K.",
      theater: "Americas",
      gl: "GL1",
      composite: 4.8,
      tier: "Champion",
      rank: 1,
    },
    {
      sponsor_name: "Maria S.",
      theater: "EMEA",
      gl: "GL2",
      composite: 4.5,
      tier: "Champion",
      rank: 2,
    },
    {
      sponsor_name: "James T.",
      theater: "APJ",
      gl: "GL1",
      composite: 4.2,
      tier: "Champion",
      rank: 3,
    },
  ],
  at_risk_sponsors: [
    {
      sponsor_name: "Chris B.",
      theater: "APJ",
      gl: "GL2",
      composite: 1.8,
      tier: "At-Risk",
      rank: 11,
      gap: "Insufficient",
    },
    {
      sponsor_name: "Lee P.",
      theater: "APJ",
      gl: "GL1",
      composite: 2.1,
      tier: "At-Risk",
      rank: 10,
      gap: "At-risk",
    },
  ],
  trend: [
    { period: "2025-Q3", avg_composite: 3.1 },
    { period: "2025-Q4", avg_composite: 3.3 },
    { period: "2026-Q1", avg_composite: 3.4 },
    { period: "2026-Q2", avg_composite: 3.5 },
    { period: "2026-Q3", avg_composite: 3.6 },
  ],
};

const ExecutiveSummaryPanel = ({
  data,
  loading = false,
}: ExecutiveSummaryPanelProps) => {
  const pageIsSample = !loading && !hasPublishedExecutiveData(data);
  const real = data ?? {};
  const sample = SAMPLE_EXECUTIVE_SUMMARY;

  // KPIs / banner from published real data when available; else full sample.
  const d: ExecutiveSummaryPayload = pageIsSample ? sample : real;

  const currentPeriod = d.current_period;
  const displayPeriod = d.display_period ?? "—";
  const isDisplayingPriorQuarter =
    Boolean(currentPeriod) &&
    Boolean(displayPeriod) &&
    currentPeriod !== displayPeriod;
  const displayPeriodLabel = formatQuarterPeriodLabel(displayPeriod);
  const currentPeriodLabel = currentPeriod
    ? formatQuarterPeriodLabel(currentPeriod)
    : "—";
  const priorPeriod = d.prior_period;
  const kpis = d.kpis ?? {};
  const kpiPeriodFootnote = isDisplayingPriorQuarter
    ? `${displayPeriodLabel} (latest published)`
    : displayPeriodLabel;

  // Per-section sample gates (when page already has published data).
  const realTierCurrent = real.tier_distribution_current ?? {};
  const realTierPrior = real.tier_distribution_prior ?? {};
  const tierIsSample =
    pageIsSample ||
    (sumTier(realTierCurrent) === 0 && sumTier(realTierPrior) === 0);
  const tierCurrent = tierIsSample
    ? (sample.tier_distribution_current ?? {})
    : realTierCurrent;
  const tierPrior = tierIsSample
    ? (sample.tier_distribution_prior ?? {})
    : realTierPrior;
  const tierChartCurrentPeriod = tierIsSample
    ? (sample.tier_chart_current_period ??
      sample.current_period ??
      currentPeriod ??
      "—")
    : (d.tier_chart_current_period ?? currentPeriod ?? "—");
  const tierChartPriorPeriod = tierIsSample
    ? (sample.tier_chart_prior_period ?? sample.prior_period)
    : (d.tier_chart_prior_period ?? priorPeriod);
  const tierChartCurrentLabel = formatQuarterPeriodLabel(
    tierChartCurrentPeriod
  );
  const tierChartPriorLabel = tierChartPriorPeriod
    ? formatQuarterPeriodLabel(tierChartPriorPeriod)
    : "—";

  const realTheaters = real.theater_breakdown ?? [];
  const theatersIsSample = pageIsSample || realTheaters.length === 0;
  const theaters = theatersIsSample
    ? (sample.theater_breakdown ?? [])
    : realTheaters;

  const realTopSponsors = real.top_sponsors ?? [];
  const topIsSample = pageIsSample || realTopSponsors.length === 0;
  const topSponsors = topIsSample
    ? (sample.top_sponsors ?? [])
    : realTopSponsors;

  const realAtRisk = real.at_risk_sponsors ?? [];
  const atRiskIsSample = pageIsSample || realAtRisk.length === 0;
  const atRisk = atRiskIsSample ? (sample.at_risk_sponsors ?? []) : realAtRisk;

  const realTrendRaw = data?.trend;
  const realTrend = useMemo(
    () => (realTrendRaw ?? []).filter(p => p.avg_composite != null),
    [realTrendRaw]
  );
  const trendIsSample = pageIsSample || realTrend.length < TREND_MIN_QUARTERS;
  const sampleTrend = sample.trend;
  const trend = useMemo(() => {
    if (trendIsSample) {
      return (sampleTrend ?? []).filter(p => p.avg_composite != null);
    }
    return realTrend;
  }, [trendIsSample, realTrend, sampleTrend]);

  const maxTierCount = useMemo(() => {
    let max = 1;
    LEADERBOARD_TIER_ORDER.forEach(t => {
      max = Math.max(max, tierCurrent[t] ?? 0, tierPrior[t] ?? 0);
    });
    return max;
  }, [tierCurrent, tierPrior]);

  if (loading) {
    return <HubPageLoading message="Loading executive summary…" />;
  }

  const avgDelta = kpis.avg_composite_delta;
  const sectionSample = !pageIsSample;

  const body = (
    <>
      {isDisplayingPriorQuarter ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-semibold">{currentPeriodLabel}</span>{" "}
          leaderboard is not published yet. Showing latest published quarter (
          <span className="font-semibold">{displayPeriodLabel}</span>).
        </div>
      ) : null}

      <div className={STATUS_CHIP_ROW}>
        <div className={execStatusChipClass("sponsors")}>
          <div className={statusChipLabelClass}>Sponsors Reporting</div>
          <div className={statusChipValueClass}>{kpis.sponsor_count ?? 0}</div>
          <div className={statusChipFootClass()}>{kpiPeriodFootnote}</div>
        </div>
        <div className={execStatusChipClass("composite")}>
          <div className={statusChipLabelClass}>Avg Composite</div>
          <div className={statusChipValueClass}>
            {fmtScore(kpis.avg_composite, 2)}
            <span className={statusChipValueUnitClass}>/ 5</span>
          </div>
          {avgDelta != null && priorPeriod ? (
            <div className={statusChipFootClass(avgDelta >= 0 ? "up" : "down")}>
              {avgDelta >= 0 ? "▲" : "▼"} {Math.abs(avgDelta).toFixed(2)} vs{" "}
              {formatQuarterPeriodLabel(priorPeriod)}
            </div>
          ) : (
            <div className={statusChipFootClass()}>{kpiPeriodFootnote}</div>
          )}
        </div>
        <div className={execStatusChipClass("champion")}>
          <div className={statusChipLabelClass}>Champion %</div>
          <div className={statusChipValueClass}>
            {kpis.champion_pct ?? 0}
            <span className={statusChipValueUnitClass}>%</span>
          </div>
          <div className={statusChipFootClass()}>
            {kpis.champion_count ?? 0} of {kpis.sponsor_count ?? 0} sponsors ·{" "}
            {kpiPeriodFootnote}
          </div>
        </div>
        <div className={execStatusChipClass("atrisk")}>
          <div className={statusChipLabelClass}>At-Risk %</div>
          <div className={statusChipValueClass}>
            {kpis.at_risk_pct ?? 0}
            <span className={statusChipValueUnitClass}>%</span>
          </div>
          <div className={statusChipFootClass()}>
            {kpis.at_risk_count ?? 0} sponsors · {kpiPeriodFootnote}
          </div>
        </div>
        <div className={execStatusChipClass("completion")}>
          <div className={statusChipLabelClass}>Completion</div>
          <div className={statusChipValueClass}>
            {kpis.completion_pct ?? 0}
            <span className={statusChipValueUnitClass}>%</span>
          </div>
          <div className={statusChipFootClass()}>
            {kpis.completion_done ?? 0} of {kpis.completion_total ?? 0} accounts
            · {kpiPeriodFootnote}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-5 min-[901px]:grid-cols-2 sm:gap-5 sm:mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-base font-bold text-gray-900">
              Tier Distribution
            </div>
            <div className="text-xs font-normal text-gray-500">
              {tierChartCurrentLabel} vs {tierChartPriorLabel}
            </div>
          </div>
          <SampleDataOverlay
            active={sectionSample && tierIsSample}
            compact
            message="Preview of tier distribution — real mix appears once ESL publishes the leaderboard."
          >
            <div className="flex flex-col gap-3">
              {LEADERBOARD_TIER_ORDER.map(tier => {
                const cur = tierCurrent[tier] ?? 0;
                const prev = tierPrior[tier] ?? 0;
                const curPct = (cur / maxTierCount) * 100;
                const prevPct = (prev / maxTierCount) * 100;
                return (
                  <div
                    key={tier}
                    className="grid grid-cols-[110px_1fr_1fr] items-center gap-2.5 text-sm sm:grid-cols-[124px_1fr_1fr] sm:gap-3"
                  >
                    <div>
                      <TierPill tier={tier} />
                    </div>
                    <div className="relative h-7 bg-gray-100 rounded-md overflow-hidden">
                      <div
                        className={tierBarFillClass(tier)}
                        style={{ width: `${curPct}%` }}
                      >
                        {cur > 0 ? cur : ""}
                      </div>
                    </div>
                    <div className="relative h-7 bg-gray-100 rounded-md overflow-hidden">
                      <div
                        className={tierBarFillClass(tier, true)}
                        style={{ width: `${prevPct}%` }}
                      >
                        {prev > 0 ? prev : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="grid grid-cols-[110px_1fr_1fr] items-center gap-2.5 text-xs font-semibold text-gray-600 sm:grid-cols-[124px_1fr_1fr] sm:gap-3 pt-1">
                <div />
                <div className="text-center">Current Quarter</div>
                <div className="text-center">Prior Quarter</div>
              </div>
            </div>
          </SampleDataOverlay>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
          <div className="text-base font-bold text-gray-900 mb-4">
            Theater Breakdown
          </div>
          <SampleDataOverlay
            active={sectionSample && theatersIsSample}
            compact
            message="Preview of theater breakdown — real theater mix appears once the leaderboard is published."
          >
            <div className="overflow-x-auto -mx-1">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200 bg-gray-50">
                      Theater
                    </th>
                    <th className="px-3 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-right border-b border-gray-200 bg-gray-50">
                      Sponsors
                    </th>
                    <th className="px-3 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-right border-b border-gray-200 bg-gray-50">
                      Avg Comp.
                    </th>
                    <th className="px-3 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wide text-left border-b border-gray-200 bg-gray-50">
                      Tier mix
                    </th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child_td]:border-b-0">
                  {theaters.map(th => {
                    const total = th.count || 1;
                    return (
                      <tr key={th.theater}>
                        <td className="px-3 py-3 border-b border-gray-100 align-middle font-semibold text-gray-800">
                          {th.theater}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100 align-middle text-right tabular-nums text-gray-700">
                          {th.count}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100 align-middle text-right tabular-nums font-semibold text-gray-900">
                          {fmtScore(th.avg_composite, 2)}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100 align-middle">
                          <div className="flex items-center h-4 bg-gray-100 rounded overflow-hidden w-full max-w-[200px]">
                            {LEADERBOARD_TIER_ORDER.map(tier => {
                              const pct = ((th.tiers[tier] ?? 0) / total) * 100;
                              if (pct <= 0) return null;
                              return (
                                <div
                                  key={tier}
                                  className={tierMixSegmentClass(tier)}
                                  style={{ width: `${pct}%` }}
                                  title={`${tier}: ${th.tiers[tier] ?? 0}`}
                                />
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SampleDataOverlay>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-5 min-[901px]:grid-cols-2 sm:gap-5 sm:mb-6">
        <div className="flex h-full flex-col bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-base font-bold text-gray-900">
              Top 5 Sponsors
            </div>
            <div className="text-xs font-normal text-gray-500">
              By composite score
            </div>
          </div>
          <SampleDataOverlay
            active={sectionSample && topIsSample}
            compact
            className="min-h-0 flex-1"
            message="Preview of top sponsors — rankings appear once the leaderboard is published."
          >
            <div className="flex flex-col gap-2.5">
              {topSponsors.map((r, i) => (
                <div
                  key={r.sponsor_id || r.sponsor_name}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm"
                >
                  <div className="w-7 h-7 rounded-md bg-purple-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">
                      {r.sponsor_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.theater} · {r.gl}
                    </div>
                  </div>
                  <div className={sponsorRowMetricsClass}>
                    <span className={sponsorScoreColumnClass}>
                      {fmtScore(r.composite, 2)}
                    </span>
                    <div className={sponsorTierColumnClass}>
                      {r.tier ? <TierPill tier={r.tier} /> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SampleDataOverlay>
        </div>

        <div className="flex h-full flex-col bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="text-base font-bold text-gray-900">
              At-Risk Watch
            </div>
            <div className="text-xs font-normal text-gray-500">
              {atRisk.length} sponsors
            </div>
          </div>
          <SampleDataOverlay
            active={sectionSample && atRiskIsSample}
            compact
            className="min-h-0 flex-1"
            message="Preview of at-risk sponsors — this list fills when published scores put sponsors in At-Risk."
          >
            <div className="flex flex-col gap-2.5">
              {atRisk.map(r => (
                <div
                  key={r.sponsor_id || r.sponsor_name}
                  className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200 text-sm"
                >
                  <div className="w-7 h-7 rounded-md bg-red-500 text-white font-bold text-xs flex items-center justify-center shrink-0">
                    {r.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">
                      {r.sponsor_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.theater} · {r.gl}
                      {r.gap ? ` · ${r.gap}` : ""}
                    </div>
                  </div>
                  <div className={sponsorRowMetricsClass}>
                    <span className={sponsorScoreColumnClass}>
                      {fmtScore(r.composite, 2)}
                    </span>
                    <div className={sponsorTierColumnClass} aria-hidden />
                  </div>
                </div>
              ))}
            </div>
          </SampleDataOverlay>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="text-base font-bold text-gray-900">
            Org Composite Trend
          </div>
          <div className="text-xs font-normal text-gray-500">
            Last 5 quarters
          </div>
        </div>
        <SampleDataOverlay
          active={sectionSample && trendIsSample}
          compact
          message="Preview of org composite trend — real chart appears once 5 published quarters are available."
        >
          <OrgCompositeTrendChart points={trend} />
        </SampleDataOverlay>
      </div>
    </>
  );

  return (
    <div className="min-w-0">
      {pageIsSample ? (
        <SampleDataOverlay
          active
          message="Preview of org-wide KPIs and sponsor rankings — real data appears once ESL publishes the leaderboard for a quarter."
        >
          {body}
        </SampleDataOverlay>
      ) : (
        body
      )}
    </div>
  );
};

export default ExecutiveSummaryPanel;
